import { RefObject } from 'react';

import styled from 'styled-components';

import { DESCRIPTION_LINE_COUNT_LIMIT } from '@components/action-card';
import { DynamicSelectOption } from '@components/form/dynamic-fields';
import { SelectOption } from '@components/select';
import { getScopedTranslate, styledTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Font } from '@styles/theme';
import { abbreviateDate } from '@utils/date';
import { formatNumber } from '@utils/numbers';

import { FormFile } from 'api/forms/requests';
import { ConditionComparator, JourneyActionValuesTypes } from 'api/types';

type ActionType = 'condition_v2';

type GetConditionDescriptionProps<Styled extends boolean = true> = {
  values: JourneyActionValuesTypes[ActionType];
  styled?: Styled;
  locale?: typeof descriptionLocale;
  isInNode?: boolean;
  isTextClamped?: boolean;
  textClampedRef?: RefObject<HTMLDivElement>;
};

type ConditionDescriptionElement = JSX.Element & {
  ref?: RefObject<any>;
};

export function getComparatorLabel(key: ConditionComparator) {
  const { t } = getScopedTranslate(descriptionLocale);
  const COMPARITOR_MAPPER: { [key in ConditionComparator]: string } = {
    eq: t('is'),
    neq: t('isNot'),
    contains: t('contains'),
    ncontains: t('notContain'),
    starts_with: t('startsWith'),
    ends_with: t('endsWith'),
    empty: t('empty'),
    nempty: t('notEmpty'),
    number_gt: t('moreThan'),
    number_lt: t('lessThan'),
    number_gte: t('moreOrEqualTo'),
    number_lte: t('lessOrEqualTo'),
    number_eq: t('equalTo'),
    number_neq: t('notEqualTo'),
    number_starts_with: t('startsWith'),
    number_ends_with: t('endsWith'),
    number_empty: t('empty'),
    number_nempty: t('notEmpty'),
    boolean_true: t('is'),
    boolean_false: t('is'),
    date_lt: t('before'),
    date_lte: t('isOrBefore'),
    date_gt: t('after'),
    date_gte: t('isOrAfter'),
    date_eq: t('is'),
    date_neq: t('isNot'),
    date_empty: t('empty'),
    date_nempty: t('notEmpty'),
    select_eq: t('is'),
    select_neq: t('isNot'),
    select_any_of: t('anyOf'),
    select_none_of: t('noneOf'),
    select_empty: t('empty'),
    select_nempty: t('notEmpty'),
    select_multi_eq: t('is'),
    select_multi_neq: t('isNot'),
    select_multi_any_of: t('anyOf'),
    select_multi_none_of: t('noneOf'),
    select_multi_empty: t('empty'),
    select_multi_nempty: t('notEmpty'),
    file_contains: t('contains'),
    file_ncontains: t('notContain'),
    file_starts_with: t('startsWith'),
    file_ends_with: t('endsWith'),
    file_empty: t('empty'),
    file_nempty: t('notEmpty'),
    changes_value: t('changesValue'),
  };
  return COMPARITOR_MAPPER[key as keyof typeof COMPARITOR_MAPPER];
}

export function getValueLabel(
  value: SelectOption | DynamicSelectOption[] | FormFile | string | undefined | number,
  comparator?: string
): string {
  const { t } = getScopedTranslate(descriptionLocale);

  if (comparator === 'boolean_true') {
    return t('booleanTrue');
  } else if (comparator === 'boolean_false') {
    return t('booleanFalse');
  } else if (
    value === undefined ||
    value === null ||
    (typeof value !== 'object' && String(value) === '')
  ) {
    return '';
  } else if (comparator?.startsWith('select_')) {
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((field: SelectOption) => field.label || '').join(', ');
    }

    return (value as SelectOption).label as string;
  } else if (comparator?.startsWith('file_')) {
    const filename = typeof value === 'string' ? value : (value as FormFile)?.name;
    return filename.split('/').pop() || '';
  } else if (comparator?.startsWith('date')) {
    return abbreviateDate(value as string, 'en');
  } else if (comparator?.startsWith('number')) {
    return formatNumber(value as number);
  }

  return ((value as SelectOption)?.label as string) ?? (value as string);
}

export function getConditionDescription(props: GetConditionDescriptionProps<false>): string;
export function getConditionDescription(
  props: GetConditionDescriptionProps<true>
): ConditionDescriptionElement;
export function getConditionDescription({
  values,
  styled = true,
  locale = descriptionLocale,
  isInNode = true,
  textClampedRef,
}: GetConditionDescriptionProps<boolean>): ConditionDescriptionElement | string {
  const { t } = getScopedTranslate(locale);

  const conditions = values?.conditions || [];
  if (conditions.length === 0) {
    return t('configureAction');
  }

  const renderConditionsCount =
    conditions.length > DESCRIPTION_LINE_COUNT_LIMIT
      ? DESCRIPTION_LINE_COUNT_LIMIT - 1
      : DESCRIPTION_LINE_COUNT_LIMIT;

  const comparatorElements = conditions.map((condition, index) => {
    if (styled && index >= renderConditionsCount) {
      return null;
    }
    const { field, comparator, value } = condition;
    const valueLabel = getValueLabel(value, comparator);
    const fieldLabel = field?.variables && Object.values(field.variables)[0].label;

    if (!fieldLabel || !comparator || (valueLabel === '' && !comparator.includes('empty'))) {
      return '';
    }

    const comparatorString = getComparatorLabel(comparator) || comparator;

    if (styled) {
      const conjunction = values?.conditionType === 'all' ? t('core.and') : t('core.or');
      const commonProps = { font: 'regular14' as Font, style: { display: 'inline' } };
      return (
        <ConditionContainer key={index}>
          {styledTranslate({
            key: 'conditionDescription',
            variables: {
              conjunction: {
                text: index > 0 ? `${conjunction} ` : '',
                color: 'textSecondary',
                capitalize: true,
                ...commonProps,
              },
              field: {
                text: fieldLabel,
                color: 'elementBlue100',
                ...commonProps,
              },
              comparator: {
                text: comparatorString,
                color: 'textSecondary',
                ...commonProps,
              },
              value: {
                text: valueLabel,
                color: 'textSecondary',
                ...commonProps,
              },
            },
            translations: descriptionLocale,
          })}
        </ConditionContainer>
      );
    } else {
      return t('conditionDescriptionNoStyle', {
        field: fieldLabel,
        comparator: comparatorString,
        value: valueLabel,
      });
    }
  });
  if (styled) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }} ref={textClampedRef}>
        {isInNode && t('continueIf')}
        {comparatorElements}
      </div>
    );
  } else {
    const conditionStrings = comparatorElements as string[];
    const joinedConditionString = conditionStrings.join(
      values?.conditionType === 'all' ? `\n${t('and')} ` : `\n${t('or')} `
    );

    return t('continueIfNoStyle', { conditions: joinedConditionString });
  }
}

type GetConditionTooltipProps = Omit<GetConditionDescriptionProps, 'styled' | 'inNode'>;

export const getConditionTooltip = (props: GetConditionTooltipProps) => {
  const { values, isTextClamped } = props;
  const conditions = values?.conditions ?? [];
  const isAllConditionsVisible =
    conditions.length <= DESCRIPTION_LINE_COUNT_LIMIT && !isTextClamped;
  return isAllConditionsVisible ? '' : getConditionDescription({ values, styled: false });
};

export const descriptionLocale = {
  en: {
    configureAction: 'Configure action',
    continueIf: 'Continue if\n',
    continueIfNoStyle: 'Continue if\n{conditions}',
    conditionDescription: '{conjunction}{field} {comparator} {value}',
    conditionDescriptionNoStyle: '{field} {comparator} {value}',
    is: 'is',
    isNot: 'is not',
    booleanTrue: 'true',
    booleanFalse: 'false',
    before: 'Before',
    isOrBefore: 'Is or before',
    isOrAfter: 'Is or after',
    after: 'After',
    and: 'AND',
    or: 'OR',
    contains: 'contains',
    notContain: 'does not contain',
    equalTo: 'equal to',
    notEqualTo: 'not equal to',
    empty: 'is empty',
    notEmpty: 'is not empty',
    anyOf: 'is any of',
    noneOf: 'is none of',
    moreThan: 'more than',
    moreOrEqualTo: 'more or equal to',
    lessThan: 'less than',
    lessOrEqualTo: 'less or equal to',
    startsWith: 'starts with',
    endsWith: 'ends with',
    changesValue: 'changes value',
  },
};
const ConditionContainer = styled.div({
  ...lineClamp('-webkit-inline-box', 1, true),
});
